import { useEffect } from 'react';
import Icon from '@/components/ui/Icon/Icon';
import type { GeocodeCandidate } from '@/lib/api.types';
import styles from './AddressPicker.module.css';

type Props = {
  open: boolean;
  candidates: GeocodeCandidate[];
  onSelect: (candidate: GeocodeCandidate) => void;
  onClose: () => void;
};

// Shown when geocoding returns several matches. The closest match is already
// provisionally selected on the map, so this is a non-blocking "did you mean…"
// that lets the buyer correct an ambiguous address before analysing the wrong spot.
export default function AddressPicker({
  open,
  candidates,
  onSelect,
  onClose,
}: Props) {
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className={styles.root}
      role="dialog"
      aria-modal="true"
      aria-labelledby="address-picker-title"
    >
      <div className={styles.backdrop} onClick={onClose} aria-hidden="true" />
      <div className={styles.panel}>
        <h2 id="address-picker-title" className={styles.title}>
          候補がいくつか見つかりました
        </h2>
        <p className={styles.message}>
          いちばん近い候補を地図に表示しています。別の場所であれば、お選びください。
        </p>
        <ul className={styles.list}>
          {candidates.map((c, i) => (
            <li key={`${c.title}-${i}`}>
              <button
                type="button"
                className={styles.item}
                onClick={() => onSelect(c)}
              >
                <Icon name="map-pin" size={18} color="var(--color-sumi)" />
                <span className={styles.itemTitle}>{c.title}</span>
                {i === 0 && <span className={styles.itemTag}>最も近い</span>}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
